import React, { useEffect, useState, useRef } from "react";
import { translateOneForce, needsForeignTranslation } from "@/lib/translate";
import { useApp } from "@/AppContext";

/**
 * <TranslatedText text="..." />
 *
 * Auto-translates user-generated content to the viewer's selected language.
 * Shows a tiny "Show original" / "Show translation" link beneath the text if
 * the translation actually differs from the source.
 *
 * Props:
 *   text          string – the original user text
 *   className     string – class for the wrapping <span>
 *   as            "span" | "p" | "div" – element type (default span)
 *   showToggle    boolean – show the "Show original" link (default true)
 *   skipShort     boolean – don't translate if text is < 4 chars (default true)
 */
export default function TranslatedText({
  text,
  className = "",
  as = "span",
  showToggle = true,
  skipShort = true,
  toggleClassName = "",
  style = undefined,
  forceLang = null,
}) {
  const { lang, authHeaders, t } = useApp();
  const [translated, setTranslated] = useState(text);
  const [showOriginal, setShowOriginal] = useState(false);
  const [different, setDifferent] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!text || (skipShort && text.trim().length < 4)) {
      setTranslated(text || "");
      setDifferent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const tt = forceLang
        ? await translateOneForce(forceLang, text, authHeaders())
        : (needsForeignTranslation(lang, text)
            ? await translateOneForce(lang, text, authHeaders())
            : text);
      if (cancelled || !mounted.current) return;
      setTranslated(tt);
      setDifferent((tt || "").trim() !== (text || "").trim());
    })();
    return () => { cancelled = true; };
  }, [text, lang, forceLang, authHeaders, skipShort]);

  const Tag = as;
  const display = showOriginal ? text : translated;
  const toggleLabel = showOriginal
    ? (t?.common?.showTranslation || "Show translation")
    : (t?.common?.showOriginal || "Show original");

  return (
    <>
      <Tag className={className} style={style} data-testid="translated-text">{display}</Tag>
      {showToggle && different && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowOriginal((s) => !s); }}
          className={`ml-1 text-[10px] text-zinc-500 hover:text-[#FF7A1A] underline decoration-dotted underline-offset-2 ${toggleClassName}`}
          data-testid="translation-toggle"
          aria-label={toggleLabel}
        >
          {toggleLabel}
        </button>
      )}
    </>
  );
}
